/** The sidebar, grouped exactly as docs/mvp-build-spec.md §9.1. Pages inside a section highlight its entry. */
import type { IconName } from "@/components";

export interface NavItem {
  label: string;
  to: string;
  icon: IconName;
  /** Match only the exact path (the dashboard at "/"). */
  end?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { label: "Home", items: [{ label: "Dashboard", to: "/", icon: "home", end: true }] },
  {
    label: "Library",
    items: [
      { label: "Materials", to: "/materials", icon: "book" },
      { label: "Published formulas", to: "/published-formulas", icon: "page" },
      { label: "Knowledge", to: "/knowledge", icon: "leaf" },
      { label: "Review", to: "/review", icon: "clipboardCheck" },
    ],
  },
  {
    label: "Stock",
    items: [
      { label: "Inventory", to: "/inventory", icon: "box" },
      { label: "Imports", to: "/imports", icon: "upload" },
    ],
  },
  {
    label: "Create",
    items: [
      { label: "Formulas", to: "/formulas", icon: "flask" },
      { label: "Compliance", to: "/compliance", icon: "shield" },
    ],
  },
  {
    label: "Bench",
    items: [
      { label: "Bench", to: "/bench", icon: "scale" },
      { label: "Evaluations", to: "/evaluations", icon: "droplet" },
      { label: "Reminders", to: "/reminders", icon: "calendar" },
      { label: "Labels", to: "/labels", icon: "tag" },
    ],
  },
  {
    label: "Buy",
    items: [
      { label: "Purchasing", to: "/purchasing", icon: "cart" },
      { label: "Supplies", to: "/supplies", icon: "vial" },
      { label: "Alerts", to: "/alerts", icon: "bell" },
      { label: "Suppliers", to: "/suppliers", icon: "truck" },
    ],
  },
  { label: "Assist", items: [{ label: "Assistant", to: "/assistant", icon: "message" }] },
  {
    label: "System",
    items: [
      { label: "Settings", to: "/settings", icon: "settings" },
      { label: "Jobs", to: "/jobs", icon: "list" },
    ],
  },
];

/** Section entry for any path: /lots/T030-24S belongs to Stock › Inventory. */
const SECTION_ALIASES: Record<string, string> = { "/lots": "/inventory" };

export function navItemFor(path: string): { group: NavGroup; item: NavItem } | null {
  const alias = Object.entries(SECTION_ALIASES).find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  const target = alias ? alias[1] : path;
  let best: { group: NavGroup; item: NavItem } | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const matches = item.end ? target === item.to : target === item.to || target.startsWith(`${item.to}/`);
      if (matches && (!best || item.to.length > best.item.to.length)) best = { group, item };
    }
  }
  return best;
}
